import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GraduationCap, X, ChevronLeft, ChevronRight, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useTrainingMode } from './TrainingModeContext';

export function TrainingGuidePanel() {
  const { isActive, currentStep, currentStepIndex, steps, nextStep, prevStep, stopTraining, projectId } = useTrainingMode();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);

  // Finishing the walkthrough hands the trainee straight to their assessment
  // rather than dropping them back on whatever screen the last step used.
  const finishTraining = () => {
    stopTraining();
    if (projectId) navigate(`/projects/${projectId}?tab=training-review`);
  };

  useEffect(() => {
    if (isActive) {
      setVisible(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setAnimateIn(true)));
    } else {
      setAnimateIn(false);
      const timer = setTimeout(() => setVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isActive]);

  if (!visible || !currentStep) return null;

  const isLastStep = currentStepIndex === steps.length - 1;

  return (
    <div
      className={`h-full shrink-0 overflow-hidden transition-[width] duration-300 ease-in-out max-w-[90vw] ${
        animateIn ? 'w-[420px]' : 'w-0'
      }`}
    >
      <div className="h-full w-[420px] bg-white shadow-xl flex flex-col border-l border-slate-200">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-amber-50">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
              <GraduationCap className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-navy">Training Mode</h2>
              <p className="text-[11px] text-slate-500">Step {currentStepIndex + 1} of {steps.length}</p>
            </div>
          </div>
          <button
            onClick={stopTraining}
            className="p-1.5 rounded-lg hover:bg-amber-100 transition-colors flex-shrink-0"
            title="Exit training mode"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-slate-100">
          <div
            className="h-full bg-amber-500 transition-all duration-300"
            style={{ width: `${((currentStepIndex + 1) / steps.length) * 100}%` }}
          />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <p className="text-[11px] font-bold text-primary tracking-widest uppercase mb-1">
            Module {currentStep.module} — {currentStep.moduleTitle}
          </p>
          <h3 className="text-lg font-bold text-navy mb-3">{currentStep.title}</h3>

          <div className="mb-4">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">What to do</p>
            <p className="text-sm text-slate-700 leading-relaxed">{currentStep.whatToDo}</p>
          </div>

          {currentStep.steps.length > 0 && (
            <ol className="mb-4 flex flex-col gap-2.5">
              {currentStep.steps.map((step, i) => (
                <li key={`${currentStep.id}-${i}`} className="flex gap-2.5 text-sm text-slate-700 leading-relaxed">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-[11px] font-bold flex items-center justify-center mt-0.5">
                    {i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          )}

          {currentStep.watchOut && currentStep.watchOut.length > 0 && (
            <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 p-3">
              <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> Watch out
              </p>
              <ul className="flex flex-col gap-2">
                {currentStep.watchOut.map((item, i) => (
                  <li key={`${currentStep.id}-warn-${i}`} className="flex gap-2 text-sm text-amber-900 leading-relaxed">
                    <span className="text-amber-500 flex-shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Why</p>
            <p className="text-sm text-slate-600 leading-relaxed">{currentStep.why}</p>
          </div>
        </div>

        {/* Footer nav */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-slate-200 bg-slate-50/50">
          <button
            onClick={prevStep}
            disabled={currentStepIndex === 0}
            className="flex items-center gap-1.5 rounded-xl h-9 px-3 border border-slate-200 bg-white text-slate-600 text-xs font-bold hover:bg-slate-50 transition-colors disabled:opacity-0 disabled:pointer-events-none"
          >
            <ChevronLeft size={14} /> Back
          </button>
          {isLastStep ? (
            <button
              onClick={finishTraining}
              className="flex items-center gap-1.5 rounded-xl h-9 px-4 bg-emerald-600 hover:bg-emerald-700 transition-colors text-white text-xs font-bold shadow-sm"
            >
              <CheckCircle2 size={14} /> Finish
            </button>
          ) : (
            <button
              onClick={nextStep}
              className="flex items-center gap-1.5 rounded-xl h-9 px-4 bg-amber-600 hover:bg-amber-700 transition-colors text-white text-xs font-bold shadow-sm"
            >
              Next <ChevronRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
